<template>
    <DsDropdown
        id="draw-date"
        :options="pastDrawOptions"
        fallback-label="Načítáme slosování..."
        v-model="selectedDraw"
        class="pp-draw-results__date-select"
        @update:model-value="selectedDateChangeHandler"
        data-test="results-date"
    />

    <DsSegmentedPicker
        v-if="enableSort"
        v-model="drawResultsSortOrder"
        :is-full-width="true"
        size="lg"
        class="pp-draw-results__sort-picker"
    >
        <PickerItem
            :title="DrawResultsPickerType.SortAscending"
            :name="DrawResultsPickerType.SortAscending"
        ></PickerItem>
        <PickerItem :title="DrawResultsPickerType.SortByDraw" :name="DrawResultsPickerType.SortByDraw"></PickerItem>
    </DsSegmentedPicker>
    <div
        :class="{ 'pp-draw-results__soft-container': showSoftContainer }"
        v-if="results && lottery === Lottery.rychla6"
    >
        <PpDrawNumbersRychla6 v-if="lottery === Lottery.rychla6" :numbers="results.numbers" />
        <PpDrawResultsButtons :lottery="lottery" />
    </div>
    <div class="pp-draw-results__results" v-else-if="results">
        <PpDrawNumbers :lottery="lottery" :numbers="results.numbers" :sortByDraw />
        <PpDrawResultsButtons :lottery="lottery" />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import DsDropdown from "@sazka/design-system/src/forms/components/ds-dropdown.vue";
import DsSegmentedPicker from "@sazka/design-system/src/segmented-picker/ds-segmented-picker.vue";
import PickerItem from "@sazka/design-system/src/segmented-picker-item/ds-segmented-picker-item.vue";
import { DsDropdownItemModel } from "@sazka/design-system/src/forms/components/ds-dropdown/ds-dropdown-models";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { getLotteryConfig } from "@sazka/lotteries/js/utils/lottery-configs/get-lottery-config";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { logNotificationException } from "@sazka/websocket/notifications/composables/use-event-logger";
import PpDrawNumbers from "@/lotteries/product-page/components/results/pp-draw-numbers.vue";
import { useLotteryResults } from "@/lotteries/services/draw-info/pp-results";
import { ProductPageAnalytics } from "./analytics/analytics-product-page";
import PpDrawNumbersRychla6 from "./components/results/pp-draw-numbers-rychla-6.vue";
import PpDrawResultsButtons from "./components/results/pp-draw-results-buttons.vue";
import { usePastDraws } from "./composables/use-past-draws";
import { usePpWagerConfiguration } from "./composables/use-wager-configuration";
import { DrawResultsPickerType } from "./model/draw-results-picker-type";

const props = defineProps<{
    lottery: string;
}>();

const { fetchPastDraws, pastDraws } = usePastDraws();
const pastDrawOptions = computed<DsDropdownItemModel[]>(
    () =>
        pastDraws.value?.map((item) => {
            return {
                label: item.name,
                value: item.id,
            };
        }) ?? []
);

const drawResultsSortOrder = ref(DrawResultsPickerType.SortAscending);

const selectedDraw = ref<string | number>();
const lottery = computed(() => LotteryParser.parseLotteryName(props.lottery));

const config = getLotteryConfig(lottery.value);
usePpWagerConfiguration().configure(config);

const enableSort = computed(() => {
    switch (lottery.value) {
        case Lottery.sportka:
        case Lottery.eurojackpot:
        case Lottery.stastnych10:
        case Lottery.euromiliony:
        case Lottery.extraRenta:
        case Lottery.miniRenta:
            return true;
        default:
            return false;
    }
});

const sortByDraw = computed(() => enableSort.value && drawResultsSortOrder.value === DrawResultsPickerType.SortByDraw);

const sm = useBreakpoints("sm");
const showSoftContainer = computed(() => lottery.value !== Lottery.rychla6 || sm.value);

const { results, fetch: fetchResults } = useLotteryResults();

const selectedDateChangeHandler = (id: string | number) => {
    fetchResults(lottery.value, Number(id));
};

ProductPageAnalytics.pushGameCheckbetPageDisplayed(LotteryParser.analyticsCodeName(lottery.value));

onMounted(async () => {
    try {
        await fetchPastDraws(lottery.value);
    } catch (error) {
        logNotificationException("Fetching past draws err", error);
        console.error("Error getting past results", error);
        return;
    }

    selectedDraw.value = pastDraws.value[0].id;
    selectedDateChangeHandler(pastDraws.value[0].id);
});
</script>
